import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Eager load landing (first paint)
import LandingPage from './pages/LandingPage';

// Lazy load everything else
const AuthPage = lazy(() => import('./pages/AuthPage'));
const BuilderChoicePage = lazy(() => import('./pages/BuilderChoicePage'));
const BuilderPage = lazy(() => import('./pages/BuilderPage'));
const CompleteSetupBuilderPage = lazy(() => import('./pages/CompleteSetupBuilderPage'));
const CustomBuildPage = lazy(() => import('./pages/CustomBuildPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PayNowPage = lazy(() => import('./pages/PayNowPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const AdvicePage = lazy(() => import('./pages/AdvicePage'));
const AIChatPage = lazy(() => import('./pages/AIChatPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(userData);
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <BrowserRouter>
      <div className="pc-builder-container">
        <Navbar user={user} onLogout={handleLogout} />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <AuthPage setUser={setUser} />} />
            <Route path="/build-choice" element={<BuilderChoicePage />} />
            <Route path="/builder" element={<BuilderPage user={user} />} />
            <Route path="/complete-setup-builder" element={<CompleteSetupBuilderPage user={user} />} />
            <Route path="/custom-build" element={user ? <CustomBuildPage user={user} /> : <Navigate to="/auth" />} />
            <Route path="/dashboard" element={user ? <DashboardPage user={user} /> : <Navigate to="/auth" />} />
            <Route path="/admin" element={user?.is_admin ? <AdminPage user={user} /> : <Navigate to="/" />} />
            <Route path="/payment/success" element={user ? <PaymentSuccessPage user={user} /> : <Navigate to="/auth" />} />
            <Route path="/payment/paynow" element={user ? <PayNowPage /> : <Navigate to="/auth" />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/advice" element={<AdvicePage />} />
            <Route path="/ai-chat" element={<AIChatPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/book" element={<BookingPage user={user} />} />
          </Routes>
        </Suspense>
        <Footer />
        <Toaster />
      </div>
    </BrowserRouter>
  );
}

export default App;
